import { alpha, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Paper, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Header from 'components/Header'
import { searchLogs, saveLog } from 'store/mentorship/log'
import { save as sendNotification } from 'store/notification/notification'
import * as snackbar from 'utils/snackbar'
import NoRecords from 'components/NoRecords'
import dayjs from 'dayjs'
import { capitalize } from 'lodash'

const statusConfig = {
  submitted: { color: '#ed6c02', muiColor: 'warning' },
  approved: { color: '#2e7d32', muiColor: 'success' },
  rejected: { color: '#d32f2f', muiColor: 'error' },
}

const formatTime = t => t ? dayjs(t, 'HH:mm:ss').format('h:mm A') : null

export default function MenteeLogsDrawer({ mentorship, onClose, isAdmin }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const list = useSelector(state => state.mentorship.log.list)
  const [selectedLog, setSelectedLog] = useState(null)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const mentorshipStandards = useSelector(state => state.lookup.types?.filter(t => t.category === 'mentorshipStandard'))

  useEffect(() => {
    if (mentorship?.id) dispatch(searchLogs({ mentorshipId: mentorship.id }))
  }, [dispatch, mentorship?.id])

  const filtered = list?.filter(log => {
    const date = dayjs(log.logDate)
    if (fromDate && date.isBefore(fromDate, 'day')) return false
    if (toDate && date.isAfter(toDate, 'day')) return false
    return true
  })

  const handleAcknowledge = async () => {
    await dispatch(saveLog({ mentorshipId: mentorship.id, id: selectedLog.id, acknowledged: true }))
    dispatch(searchLogs({ mentorshipId: mentorship.id }))
    setSelectedLog(null)
  }

  const notifyRejection = async logId => {
    const result = await dispatch(sendNotification({
      templateCode: 'mentorship-log-rejection',
      payload: [{ _id: logId }],
    }))

    if (result?.error) {
      snackbar.error(result.error?.data?.message || t('Log rejected, but the mentor could not be notified.'))
      return
    }

    snackbar.success(t('Mentor notified that the log was rejected.'))
  }

  const handleAction = async (status, feedback) => {
    const logId = selectedLog.id
    const result = await dispatch(saveLog({
      mentorshipId: mentorship.id,
      id: logId,
      status,
      ...(feedback !== undefined && { feedback }),
    }))

    if (result?.error) {
      snackbar.error(result.error?.data?.message || t('Could not update the log.'))
      return
    }

    if (status === 'rejected') await notifyRejection(logId)

    dispatch(searchLogs({ mentorshipId: mentorship.id }))
    setSelectedLog(null)
  }

  const handleRejectClick = () => {
    setRejectNote('')
    setShowRejectDialog(true)
  }

  const handleConfirmReject = async () => {
    setShowRejectDialog(false)
    await handleAction('rejected', rejectNote)
  }

  const menteeName = mentorship?.mentee
    ? `${mentorship.mentee.firstName} ${mentorship.mentee.lastName}`
    : ''

  const selected = selectedLog
    ? (list?.find(l => l.id === selectedLog.id) ?? selectedLog)
    : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={menteeName} subtitle={t('Mentee Logs')} small close={onClose} borderBottom />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <DatePicker
            label={t('From')}
            value={fromDate}
            onChange={setFromDate}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
            sx={{ flex: 1 }}
          />
          <DatePicker
            label={t('To')}
            value={toDate}
            onChange={setToDate}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
            sx={{ flex: 1 }}
          />
        </Stack>

        {list === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {filtered?.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <NoRecords message={t('No logs found')} />
          </Box>
        )}

        <Stack spacing={1.5}>
          {filtered?.map(log => {
            const { color, muiColor } = statusConfig[log.status] || { color: '#9e9e9e', muiColor: 'default' }
            const startTime = formatTime(log.startTime)
            const endTime = formatTime(log.endTime)

            return (
              <Paper
                key={log.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: theme => `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                <Stack direction="row" alignItems="stretch">
                  <Box
                    sx={{
                      width: 64,
                      flexShrink: 0,
                      bgcolor: alpha(color, 0.1),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      borderRight: `1px solid ${alpha(color, 0.2)}`,
                    }}
                  >
                    <Typography variant="h5" fontWeight={800} lineHeight={1} sx={{ color }}>
                      {dayjs(log.logDate).format('D')}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {dayjs(log.logDate).format('MMM')}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color }}>
                      {dayjs(log.logDate).format('YYYY')}
                    </Typography>
                  </Box>

                  <Stack direction="row" alignItems="center" sx={{ flex: 1, px: 2, py: 1.25 }} spacing={1.5}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {dayjs(log.logDate).format('dddd')}
                      </Typography>
                      {startTime || endTime ? (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                          <AccessTimeIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {[startTime, endTime].filter(Boolean).join(' – ')}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">{t('No time recorded')}</Typography>
                      )}
                    </Box>

                    {log.acknowledged && <CheckCircleIcon sx={{ fontSize: 16, color: 'info.main', flexShrink: 0 }} />}
                    <Chip label={t(log.status?.toUpperCase())} color={muiColor} size="small" sx={{ fontWeight: 600, flexShrink: 0 }} />

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedLog(log)}
                      sx={{ minWidth: 64, flexShrink: 0 }}
                    >
                      {t('More')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      </Box>

      <Drawer
        open={Boolean(selected)}
        anchor="right"
        onClose={() => setSelectedLog(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        {selected && (() => {
          const { muiColor } = statusConfig[selected.status] || { muiColor: 'default' }
          const startTime = formatTime(selected.startTime)
          const endTime = formatTime(selected.endTime)
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Header title={t('Log Details')} small close={() => setSelectedLog(null)} borderBottom />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: '14px !important' }} />}
                  label={dayjs(selected.logDate).format('MMM D, YYYY')}
                  size="small"
                  variant="outlined"
                />
                {(startTime || endTime) && (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                    label={[startTime, endTime].filter(Boolean).join(' – ')}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" >
                  <Chip label={t(`Status: ${capitalize(selected.status)}`)} color={muiColor} size="small" sx={{ fontWeight: 700 }} />
                  {selected.acknowledged &&
                    <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label={t('Mentor Acknowledged')} size="small" color="info" variant="outlined" />
                  }
                </Stack>
                <Typography variant="overline" color="text.secondary">{t('Comment')}</Typography>
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {selected.content || '—'}
                  </Typography>
                </Box>

                {selected.feedback && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="overline" color="text.secondary">{t('Rejection Feedback')}</Typography>
                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                        {selected.feedback}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selected.standardCodes?.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="overline" color="text.secondary">{t('Standards')}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      {selected.standardCodes.map(code => {
                        const standard = mentorshipStandards?.find(s => s.code === code)
                        return <Chip key={code} label={standard ? `[${capitalize(standard.code)}] - ${standard.name}` : code} size="small" variant="outlined" />
                      })}
                    </Box>
                  </Box>
                )}
              </Box>

              {isAdmin ? (
                selected.status === 'submitted' && (
                  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Stack spacing={1}>
                      <Button fullWidth variant="contained" color="success" onClick={() => handleAction('approved')}>
                        {t('Approve')}
                      </Button>
                      <Button fullWidth variant="outlined" color="error" onClick={handleRejectClick}>
                        {t('Reject')}
                      </Button>
                    </Stack>
                  </Box>
                )
              ) : (
                !selected.acknowledged && (
                  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button fullWidth variant="contained" onClick={handleAcknowledge}>
                      {t('Acknowledge')}
                    </Button>
                  </Box>
                )
              )}
            </Box>
          )
        })()}
      </Drawer>

      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('Reject Log')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label={t('Rejection Note')}
            placeholder={t('Explain why this log is being rejected...')}
            value={rejectNote}
            onChange={e => setRejectNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>{t('Cancel')}</Button>
          <Button variant="contained" color="error" disabled={!rejectNote.trim()} onClick={handleConfirmReject}>
            {t('Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
